import { Controller, useForm } from "react-hook-form";

import WajeezLogo from "@components/items/common/WajeezLogo";
import { ShadcnButton } from "@components/items/display/ShadcnButton";
import TranslatedText from "@components/items/display/TranslatedText";
import { Input } from "@ui/input";
import { Label } from "@ui/label";
import { Textarea } from "@ui/textarea";
import { emailRegex } from "@utils/helpers/emailValidation";
import { ResourcesKey, translate } from "@modules/localizations/translate";
import { contactUs, setAlert, useContactUs } from "@lib/stores";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";
import { AlertType } from "@lib/stores/common/types";

type Inputs = {
  email: string;
  text: string;
};

const ContactUsPage = () => {
  const navigate = useAppNavigate();

  const { status } = useContactUs();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      email: "",
      text: "",
    },
  });

  const onSubmit = (data: Inputs) => {
    contactUs({ email: data.email, text: data.text, tags: [] }, () => {
      navigate("/help-center");

      setAlert({
        show: true,
        for: "page",
        type: AlertType.SUCCESS,
        messages: [
          {
            code: 0,
            field: null,
            message: translate("help-center.titles.contact us success message"),
          },
        ],
      });
    });
  };

  return (
    <div className="cus-scrollbar-w-none h-full overflow-y-auto p-10 text-black dark:text-white">
      <div className="flex items-center justify-start gap-x-8">
        <WajeezLogo width={100} />
        <TranslatedText
          text={`help-center.titles.${"contact us"}`}
          className="text-[24px] font-bold"
        />
      </div>
      <form className="space-y-8 px-16 py-12" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col items-start justify-center space-y-6">
          <Label>
            <TranslatedText
              text={`help-center.labels.${"email"}`}
              className="text-[16px] font-semibold"
            />
          </Label>
          <Controller
            name="email"
            control={control}
            rules={{
              required: { value: true, message: "this field is required" },
              pattern: {
                value: emailRegex(),
                message: "invalid email",
              },
            }}
            render={({ field }) => (
              <div className="w-full">
                <Input
                  id="email"
                  className={`min-h-12 !bg-[#fafafa] dark:!bg-[#37383F] ${
                    errors?.email?.message?.length &&
                    "border-2 border-red-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                  }`}
                  placeholder="name@gmail.com"
                  {...field}
                />
                <TranslatedText
                  text={
                    `help-center.errors.${errors?.email?.message}` as ResourcesKey
                  }
                  className="pl-2 text-[12px] text-red-500"
                />
              </div>
            )}
          />
        </div>
        <div className="flex flex-col items-start justify-center space-y-6">
          <Label>
            <TranslatedText
              text={`help-center.labels.${"tell us more"}`}
              className="text-[16px] font-semibold"
            />
          </Label>
          <Controller
            name="text"
            control={control}
            rules={{
              required: { value: true, message: "this field is required" },
            }}
            render={({ field }) => (
              <div className="w-full">
                <Textarea
                  rows={10}
                  id="text"
                  className={`!bg-[#fafafa] dark:!bg-[#37383F] ${
                    errors?.text?.message?.length &&
                    "border-2 border-red-500 focus-visible:ring-0 focus-visible:ring-offset-0"
                  }`}
                  {...field}
                />
                <TranslatedText
                  text={
                    `help-center.errors.${errors?.text?.message}` as ResourcesKey
                  }
                  className="pl-2 text-[12px] text-red-500"
                />
              </div>
            )}
          />
        </div>
        <div className="flex">
          <ShadcnButton
            className="mx-auto text-white"
            isLoading={status === "loading"}
          >
            <TranslatedText
              text={`help-center.buttons.${"send"}`}
              className="text-[16px] font-medium"
            />
          </ShadcnButton>
        </div>
      </form>
    </div>
  );
};

export default ContactUsPage;
